import type React from "react";

import { Stack } from "@mui/material";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import type { DurationIcon, DurationUnit } from "api/services/content/schemas/shared";
import type { DurationWeeks } from "api/services/treatment/schemas/Treatment";
import { LevelLockButton } from "routes/patients/views/Treatment/components/TreatmentEditor/shared/styles";
import type {
  HandleLevelChange,
  HandleSetsAndRepsChange,
  HandleWeekRangeChange,
} from "routes/patients/views/Treatment/components/TreatmentEditor/types";
import { MUISelect } from "shared/atoms/MUI";
import { useSecondsOptions, useSetsOptions } from "utils/form";
import { useMinutesOptions, useRepsOptions } from "utils/form/options";

interface ExerciseControlsExercise {
  exercise_id: number;
  max_level?: number;
  recommended_level: number;
  level_locked?: boolean;
  recommended_sets?: number | null;
  recommended_reps?: string | null;
  sets: number;
  reps: string;
  duration_icon?: DurationIcon | null;
  duration_unit?: DurationUnit | null;
  weeks?: number[];
}

interface ExerciseControlsProps {
  exercise: ExerciseControlsExercise;
  durationWeeks?: DurationWeeks;
  highestWeekNumber?: number;
  isDisabled?: boolean;
  onLevelChange?: HandleLevelChange;
  onSetsAndRepsChange?: HandleSetsAndRepsChange;
  onToggleLevelLock?: HandleLevelChange;
  onWeekRangeChange?: HandleWeekRangeChange;
}

export const ExerciseControls: React.FC<ExerciseControlsProps> = ({
  durationWeeks,
  exercise,
  highestWeekNumber,
  isDisabled,
  onLevelChange,
  onSetsAndRepsChange,
  onToggleLevelLock,
  onWeekRangeChange,
}) => {
  const { t } = useTranslation();
  const setsOptions = useSetsOptions();
  const repsOptions = useRepsOptions();
  const minutesOptions = useMinutesOptions();
  const secondsOptions = useSecondsOptions();

  const showWeekSelectors = durationWeeks !== undefined && highestWeekNumber !== undefined;
  const startWeekOptions = showWeekSelectors
    ? Array.from({ length: durationWeeks || highestWeekNumber }, (_, i) => ({
        label: t("common.week_num", { number: i + 1 }),
        value: i + 1,
      }))
    : [];
  const endWeekOptions: Array<{ label: string; value: number | "ongoing" }> = showWeekSelectors
    ? [{ label: t("common.ongoing"), value: "ongoing" }, ...startWeekOptions]
    : [];

  const sets = exercise.recommended_sets ? exercise.recommended_sets : exercise.sets;
  const reps = exercise.recommended_reps ? exercise.recommended_reps : exercise.reps;
  const weeks = exercise.weeks ?? [];

  return (
    <Stack direction="row" spacing={2} sx={{ justifyContent: "space-between", flexWrap: "wrap" }}>
      {showWeekSelectors && (
        <Stack direction="row" spacing={1}>
          <MUISelect
            label={t("library.exercises.fields.start_week")}
            options={startWeekOptions}
            value={weeks.length > 0 ? Math.min(...weeks) : undefined}
            onChange={event => {
              onWeekRangeChange?.({
                exercise_id: exercise.exercise_id,
                targetWeek: event.target.value as number,
                type: "start",
              });
            }}
            size="small"
          />

          <MUISelect
            label={t("library.exercises.fields.end_week")}
            options={endWeekOptions.filter(week => {
              const startWeek = weeks.length > 0 ? Math.min(...weeks) : 1;
              return week.value === "ongoing" || (typeof week.value === "number" && week.value >= startWeek);
            })}
            value={(() => {
              if (weeks.length === 0) return undefined;
              const maxWeek = Math.max(...weeks);
              return maxWeek === durationWeeks ? "ongoing" : maxWeek;
            })()}
            onChange={event => {
              const { value } = event.target;
              const targetWeek = value === "ongoing" ? durationWeeks || highestWeekNumber : Number(value);
              onWeekRangeChange?.({
                exercise_id: exercise.exercise_id,
                targetWeek: targetWeek as number,
                type: "end",
              });
            }}
            size="small"
          />
        </Stack>
      )}

      <Stack direction="row" spacing={2} sx={{ marginLeft: "auto" }}>
        {exercise?.max_level && exercise.max_level > 1 && (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <MUISelect
              disabled={isDisabled}
              label={t("usercourse.level")}
              options={Array.from({ length: exercise.max_level ?? 1 }, (_, i) => ({
                label: `${i + 1}`,
                value: i + 1,
              }))}
              value={exercise.recommended_level || 1}
              onChange={event => {
                onLevelChange?.({
                  exercise_id: exercise.exercise_id,
                  recommended_level: event.target.value as number,
                });
              }}
              size="small"
            />
            <LevelLockButton
              className={clsx({ active: exercise.level_locked, large: true })}
              disabled={isDisabled}
              variant="action"
              icon={exercise.level_locked ? "lock" : "lock-open"}
              size="small"
              title={exercise.level_locked ? t("treatment.editor.unlock_level") : t("treatment.editor.lock_level")}
              onClick={event => {
                event.stopPropagation();
                onToggleLevelLock?.({
                  exercise_id: exercise.exercise_id,
                  recommended_level: exercise.recommended_level || 1,
                });
              }}
            />
          </Stack>
        )}

        <Stack direction="row" spacing={1}>
          <MUISelect
            disabled={isDisabled}
            label={t("library.exercises.fields.sets")}
            options={setsOptions}
            value={sets}
            onChange={event => {
              const recommendedSetsValue = event.target.value as number;
              const recommendedSets = exercise.sets !== recommendedSetsValue ? recommendedSetsValue : null;
              onSetsAndRepsChange?.({
                exercise_id: exercise.exercise_id,
                recommended_sets: recommendedSets,
              });
            }}
            size="small"
          />

          {exercise.duration_icon === "timer" ? (
            <MUISelect
              disabled={isDisabled}
              label={t("common.duration")}
              options={exercise.duration_unit === "minutes" ? minutesOptions : secondsOptions}
              value={reps}
              onChange={event => {
                const recommendedRepsValue = event.target.value as string;
                const recommendedReps = exercise.reps !== recommendedRepsValue ? recommendedRepsValue : null;
                onSetsAndRepsChange?.({
                  exercise_id: exercise.exercise_id,
                  recommended_reps: recommendedReps,
                });
              }}
              size="small"
            />
          ) : (
            <MUISelect
              disabled={isDisabled}
              label={t("library.exercises.fields.reps")}
              options={repsOptions}
              value={reps}
              onChange={event => {
                const recommendedRepsValue = event.target.value as string;
                const recommendedReps = exercise.reps !== recommendedRepsValue ? recommendedRepsValue : null;
                onSetsAndRepsChange?.({
                  exercise_id: exercise.exercise_id,
                  recommended_reps: recommendedReps,
                });
              }}
              size="small"
            />
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
